import { Controller } from 'stimulus'
import $ from 'jquery'
import { replaceWithPreserves } from '~/common/helpers/replace'
import { updateUrl } from '~/common/helpers/update_url'
import { AjaxHelper } from '~/common/extensions/ajax_helpers'
import { uniqueNewRecords } from './cursor_paginator_component_helpers.js'

const target = (name) => `[data-cursor-paginator-target=${name}]`

export default class extends Controller {
  static values = {
    url: String,
    updateBrowserUrl: Boolean,
    loadOnInitialize: Boolean,
  }

  static targets = [
    'header',
    'emptyState',
    'recordsContainer',
    'loaderContainer',
    'urlChangedLoaderTemplate',
  ]

  initialize() {
    if (this.loadOnInitializeValue) {
      this.loadOnInitializeValue = false
      setTimeout(() => this.loadNextPage(), 10)
    }
  }

  loadNextPage() {
    if (this.hasLoaderContainerTarget) {
      this.dispatch('unobserve', { target: this.loaderContainerTarget })
      this.dispatch('manual-load', { target: this.loaderContainerTarget })
    }
  }

  loadFromNextCursor(event) {
    this.#load({ cursor: event.params.nextCursor })
  }

  // The loader's intersection-observer is a separate controller that loads on its own. When its
  // file arrives before ours, its one-time "appear" fires while nobody listens, and the spinner
  // spins forever. It still marks the loader visible, so load the page when we connect.
  loaderContainerTargetConnected(loader) {
    if (loader.hasAttribute('isvisible')) {
      this.#load({ cursor: loader.dataset.cursorPaginatorNextCursorParam })
    }
  }

  /**
   * Change params and reload the recrods.
   * @param {Object} event
   *   @param {string} event.detail.name - Name of the param
   *   @param {string} event.detail.value - THe value. IF this is an object, then we'll add "name[property]=value" to the URL for each property/value pair.
   */
  changeParam(event) {
    const name = event.detail.name
    const value = event.detail.value

    const newUrl = updateUrl(this.urlValue, name, value)
    if (!newUrl) return

    this.urlValue = newUrl
    this.#reload()
  }

  refresh() {
    this.#reload()
  }

  insertNewLoader(loaderHtml) {
    if (this.hasLoaderContainerTarget) this.loaderContainerTarget.remove()
    $(this.recordsContainerTarget).after(loaderHtml)
  }

  // Reloading from page one — a search keystroke, a filter change, an order change.
  //
  // The records on screen are deliberately left alone until the response lands. Emptying them up
  // front and waiting for the freshly inserted loader to scroll into view meant every debounced
  // keystroke paid for a forced layout (to pin minHeight), the teardown of every row and the
  // Stimulus controllers, ResizeObservers and tippy instances hanging off them, an extra frame
  // waiting on the IntersectionObserver, a spinner flash, and then a rebuild from scratch. That is
  // where the Safari jank came from.
  //
  // `event.detail.keepHeader`, which callers still pass, only ever suppressed the intermediate
  // blanking of the header — nothing is blanked now, so there is nothing left for it to do. The
  // header is replaced from the response either way, exactly as before.
  #reload() {
    if (this.updateBrowserUrlValue) {
      window.history.replaceState(
        window.history.state,
        document.title,
        this.urlValue
      )
    }

    // The existing next-page loader is left in the DOM (and thus still observed) until the
    // replacement response lands. If it scrolls/appears while that request is pending, it would
    // fire loadFromNextCursor with its now-stale cursor, aborting this replace request and
    // appending stale records instead of showing page one for the new params. Unobserve it first,
    // same as the manual "load next page" path does.
    if (this.hasLoaderContainerTarget) {
      this.dispatch('unobserve', { target: this.loaderContainerTarget })
    }

    this.#load({ cursor: null, replace: true })
  }

  #load({ cursor, replace = false }) {
    this.lastLoadQuery?.abort?.()

    const query = $.ajax({
      type: 'GET',
      url: this.urlValue,
      data: cursor ? { cursor } : {},
      global: false,
      cache: false,
    })
    this.lastLoadQuery = query

    if (replace) this.element.setAttribute('aria-busy', 'true')

    query
      .then((data) => {
        if (this.lastLoadQuery !== query) return
        this.#render(data, { replace })
      })
      .catch((error) => {
        // A superseded query is the normal case while typing in a search box — we aborted it
        // ourselves, so it isn't worth reporting.
        if (this.lastLoadQuery !== query) return
        console.error(
          'Error while doing a cursor paginate:',
          error,
          error?.responseText
        )
        this.dispatch('loadFailed')
      })

    query.always(() => {
      if (this.lastLoadQuery === query)
        this.element.removeAttribute('aria-busy')
    })
  }

  #render(data, { replace }) {
    const content = this.#parse(data.content)

    const newHeader = content.querySelector(target('header'))
    if (newHeader && this.hasHeaderTarget) {
      replaceWithPreserves($(this.headerTarget), newHeader)
    }

    const newRecords = content.querySelector(target('recordsContainer'))
    const incoming = newRecords ? Array.from(newRecords.childNodes) : []
    const reset = replace || newRecords?.dataset.resetOnLoad === 'true'
    const records = reset
      ? incoming
      : uniqueNewRecords(this.recordsContainerTarget.children, incoming)

    if (reset) this.recordsContainerTarget.replaceChildren()
    // jQuery's append rather than the native one: it executes any <script> the rendered rows
    // carry, which the native append would silently skip.
    $(this.recordsContainerTarget).append(records)
    $(records).changed?.()

    if (data.extra) {
      AjaxHelper.doUpdate(data.extra)
    }

    this.insertNewLoader(this.#findLoader(content, newRecords))
    this.dispatch('loadComplete')
  }

  // Native parsing instead of $(html) + .find(): jQuery runs Sizzle over the detached nodes, which
  // is both slower and pointless when querySelectorAll does the job.
  //
  // It has to be a plain <div> and NOT a <template>. A template's content belongs to an inert
  // "template contents owner document" that has no documentElement, and jQuery's isAttached() —
  // reached from every append/replaceWith we hand these nodes to — evaluates
  // `elem.ownerDocument.documentElement.contains(…)`, so it dies with "null is not an object
  // (evaluating 'adown.contains')". A div created from `document` owns its parsed children
  // outright, which is exactly the container jQuery's own parser used before this.
  #parse(html) {
    const container = document.createElement('div')
    container.innerHTML = html
    return container
  }

  // Our own next-page loader is the one that is neither the copy the component keeps in a
  // <template> nor a nested paginator's — a paginator rendered inside one of our rows sits
  // earlier in document order than ours, so plain document order would pick the wrong one.
  #findLoader(content, newRecords) {
    return Array.from(content.querySelectorAll(target('loaderContainer'))).find(
      (element) =>
        !element.closest('template') && !newRecords?.contains(element)
    )
  }
}
